import { useState } from "react";
import { ConfirmDialog } from "@/components/confirm-dialog";
import { MachineTable } from "@/components/machine-table";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/auth-context";
import {
  useCapacityBlockGroups,
  useCbgMachines,
  useStartMachine,
  useSyncCbgs,
} from "@/hooks/use-capacity";
import { usePageTitle } from "@/hooks/use-page-title";
import type { CapacityBlockGroup, Machine } from "@/lib/types";
import { cn } from "@/lib/utils";

function CbgStatusBadge({ status }: { status: string }) {
  const colorClass =
    status === "active" ? "bg-emerald-50 text-emerald-700" : "bg-muted text-muted-foreground";

  return (
    <span className={cn("inline-block rounded-md px-2 py-0.5 text-[11px] font-medium", colorClass)}>
      {status}
    </span>
  );
}

function parseGpuType(gpuPlatform: string): string {
  return gpuPlatform.replace("gpu-", "").replace("-sxm", "").toUpperCase();
}

function StartButton({ machine, cbgId }: { machine: Machine; cbgId: number }) {
  const startMachine = useStartMachine();

  if (machine.cloud_status !== "stopped") return null;

  return (
    <ConfirmDialog
      title={`Start ${machine.name}?`}
      description="The instance will be started from its stopped state."
      confirmLabel="Start"
      disabled={startMachine.isPending}
      onConfirm={() => startMachine.mutate({ cbgId, machineId: machine.id })}
      trigger={
        <Button
          size="sm"
          variant="outline"
          disabled={startMachine.isPending}
          className="h-7 border-emerald-300 bg-emerald-50 text-[12px] text-emerald-700 hover:bg-emerald-100 hover:text-emerald-800"
        >
          {startMachine.isPending ? "Starting..." : "Start"}
        </Button>
      }
    />
  );
}

function CbgMachinesSection({ cbgId }: { cbgId: number }) {
  const { user } = useAuth();
  const { data: machines = [], isLoading } = useCbgMachines(cbgId);

  if (isLoading) {
    return (
      <div className="py-4 text-center text-[12px] text-muted-foreground">Loading instances...</div>
    );
  }

  if (machines.length === 0) {
    return <div className="py-4 text-center text-[12px] text-muted-foreground">No instances</div>;
  }

  return (
    <MachineTable
      machines={machines}
      currentUserId={user?.id ?? 0}
      isAdmin={user?.is_admin}
      extraActions={(machine) => <StartButton machine={machine} cbgId={cbgId} />}
      stickyHeaderClassName="bg-card"
    />
  );
}

function CbgCard({ cbg }: { cbg: CapacityBlockGroup }) {
  const [expanded, setExpanded] = useState(true);
  const { data: machines = [] } = useCbgMachines(cbg.id);
  const gpuType = parseGpuType(cbg.gpu_platform);
  const fleetGpuCount = machines
    .filter((machine) => machine.cloud_status === "running" || machine.cloud_status === "starting")
    .reduce((sum, machine) => sum + machine.gpu_count, 0);
  const usagePercent =
    cbg.gpu_count_total > 0 ? Math.round((cbg.gpu_count_consumed / cbg.gpu_count_total) * 100) : 0;

  return (
    <div className="rounded-xl border border-border bg-card shadow-sm">
      {/* Header */}
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="flex w-full flex-col gap-3 rounded-t-xl px-3 py-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4 sm:px-5 sm:py-4 text-left transition-colors hover:bg-accent"
      >
        <div className="flex items-center gap-3">
          <div>
            <div className="flex items-center gap-2">
              <span className="font-mono text-[14px] font-semibold text-foreground">{gpuType}</span>
              <span className="text-[13px] text-muted-foreground">&middot; {cbg.region}</span>
              {cbg.fabric && (
                <span className="text-[13px] text-muted-foreground">&middot; {cbg.fabric}</span>
              )}
              <CbgStatusBadge status={cbg.status} />
            </div>
            <div className="mt-0.5 font-mono text-[11px] text-muted-foreground">
              {cbg.name} &middot; {cbg.nebius_id}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-4">
          <div className="text-right">
            <div
              className={cn(
                "font-mono text-[14px] font-semibold",
                usagePercent > 80
                  ? "text-amber-600"
                  : usagePercent > 0
                    ? "text-blue-600"
                    : "text-muted-foreground",
              )}
            >
              {cbg.gpu_count_consumed} / {cbg.gpu_count_total} GPUs
            </div>
            <div className="text-[11px] text-muted-foreground">
              {fleetGpuCount !== cbg.gpu_count_consumed
                ? `${fleetGpuCount} in fleet \u00b7 ${cbg.gpu_count_consumed - fleetGpuCount} external`
                : `${usagePercent}% used`}
            </div>
          </div>
          {/* Usage bar */}
          <div className="h-2 w-16 rounded-full bg-muted sm:w-24">
            <div
              className={cn(
                "h-2 rounded-full transition-all",
                usagePercent > 80 ? "bg-amber-500" : "bg-blue-500",
              )}
              style={{ width: `${usagePercent}%` }}
            />
          </div>
          <svg
            aria-hidden="true"
            className={cn(
              "h-4 w-4 text-muted-foreground transition-transform",
              expanded && "rotate-180",
            )}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </button>

      {/* Expanded content */}
      {expanded && (
        <div className="border-t border-border px-3 pb-3 pt-2 sm:px-5 sm:pb-4">
          <CbgMachinesSection cbgId={cbg.id} />
        </div>
      )}
    </div>
  );
}

export function ReservedCapacityPage() {
  usePageTitle("Reserved Capacity");
  const { data: cbgs = [], isLoading } = useCapacityBlockGroups();
  const syncCbgs = useSyncCbgs();

  return (
    <div className="px-4 py-4 sm:px-6 sm:py-5">
      {/* Header */}
      <div className="mb-4 flex items-center justify-between">
        <h1 className="text-[15px] font-semibold text-foreground">Reserved Capacity</h1>
        <Button
          variant="outline"
          size="sm"
          disabled={syncCbgs.isPending}
          onClick={() => syncCbgs.mutate()}
          className="border-border bg-card text-foreground/80 hover:bg-accent hover:text-foreground"
        >
          {syncCbgs.isPending ? "Syncing..." : "Sync"}
        </Button>
      </div>

      {/* Content */}
      {isLoading ? (
        <div className="flex items-center justify-center py-20">
          <span className="text-sm text-muted-foreground">Loading capacity block groups...</span>
        </div>
      ) : cbgs.length === 0 ? (
        <div className="flex items-center justify-center py-20">
          <span className="text-sm text-muted-foreground">
            No capacity block groups found. Click Sync to fetch from Nebius.
          </span>
        </div>
      ) : (
        <div className="space-y-4">
          {cbgs.map((cbg) => (
            <CbgCard key={cbg.id} cbg={cbg} />
          ))}
        </div>
      )}
    </div>
  );
}
